import { useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { OutletProvider, useOutlet } from './contexts/OutletContext';
import { MdStore, MdLogout, MdSwapHoriz, MdPolicy, MdDevices } from 'react-icons/md';
import { FiUser } from 'react-icons/fi';
import LoginView from './views/LoginView';
import OutletSelectionView from './views/OutletSelectionView';
import DevicesView from './views/DevicesView';
import PoliciesView from './views/PoliciesView';

// Main Layout Component (needs access to OutletContext)
function MainLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, merchantData, logout } = useAuth();
  const { selectedOutlet, clearSelection } = useOutlet();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      clearSelection();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleSwitchOutlet = () => {
    clearSelection();
    navigate('/select-outlet');
    setDropdownOpen(false);
  };

  const displayName = merchantData?.name || currentUser?.uid || 'Admin';
  const displayId = merchantData?.merchantId || '';

  return (
    <div className="min-vh-100 bg-light">
      {/* Top Navbar */}
      <nav className="navbar navbar-expand-lg navbar-dark bg-primary shadow-sm sticky-top">
        <div className="container-fluid">
          {/* Brand */}
          <a className="navbar-brand fw-bold" href="#">
            <MdStore className="me-2" size={24} />
            MDM Portal
          </a>

          {/* Selected Outlet Info */}
          {selectedOutlet && (
            <div className="text-white me-auto ms-3">
              <small className="opacity-75 d-block" style={{ fontSize: '0.75rem' }}>Current Outlet</small>
              <div className="fw-semibold" style={{ fontSize: '0.9rem' }}>{selectedOutlet.name}</div>
            </div>
          )}

          {/* Navigation Buttons */}
          <button
            className={`btn ${
              location.pathname === '/devices' ? 'btn-light' : 'btn-outline-light'
            } me-2 d-flex align-items-center`}
            onClick={() => navigate('/devices')}
          >
            <MdDevices className="me-2" size={20} />
            Devices
          </button>

          <button
            className={`btn ${
              location.pathname === '/policies' ? 'btn-light' : 'btn-outline-light'
            } me-2 d-flex align-items-center`}
            onClick={() => navigate('/policies')}
          >
            <MdPolicy className="me-2" size={20} />
            Policies
          </button>

          {/* User Dropdown */}
          <div className="dropdown">
            <button 
              className="btn btn-light dropdown-toggle d-flex align-items-center"
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              <FiUser className="me-2" />
              <span className="d-none d-md-inline">{displayName}</span>
            </button>
            
            <ul className={`dropdown-menu dropdown-menu-end ${dropdownOpen ? 'show' : ''}`}>
              <li><h6 className="dropdown-header">{displayName}</h6></li>
              {displayId && <li><span className="dropdown-item-text text-muted small">{displayId}</span></li>}
              {selectedOutlet && (
                <>
                  <li><hr className="dropdown-divider" /></li>
                  <li>
                    <button className="dropdown-item" onClick={handleSwitchOutlet}>
                      <MdSwapHoriz className="me-2" />
                      Switch Outlet
                    </button>
                  </li>
                </>
              )}
              <li><hr className="dropdown-divider" /></li>
              <li>
                <button className="dropdown-item text-danger" onClick={handleLogout}>
                  <MdLogout className="me-2" />
                  Logout
                </button>
              </li>
            </ul>
          </div>
        </div>
      </nav>

      {/* Page Content */}
      <div className="container-fluid p-4">
        <Routes>
          <Route path="/" element={<Navigate to="/devices" replace />} />
          <Route path="/devices" element={<DevicesView />} />
          <Route path="/policies" element={<PoliciesView />} />
          <Route path="/select-outlet" element={<OutletSelectionView />} />
          <Route path="/login" element={<Navigate to="/devices" replace />} />
          <Route path="*" element={<Navigate to="/devices" replace />} />
        </Routes>
      </div>
    </div>
  );
}

// Main App Component
function App() {
  const { currentUser, merchantData } = useAuth();

  // Show login page for unauthenticated users
  if (!currentUser) {
    return (
      <Routes>
        <Route path="/login" element={<LoginView />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Wrap authenticated users with OutletProvider
  return (
    <OutletProvider merchantData={merchantData}>
      <OutletSelectorWrapper />
    </OutletProvider>
  );
}

// Wrapper to check if outlet is selected
function OutletSelectorWrapper() {
  const { selectedOutlet } = useOutlet();
  const location = useLocation();

  // If no outlet selected and not on outlet selection page, redirect
  if (!selectedOutlet && location.pathname !== '/select-outlet') {
    return <Navigate to="/select-outlet" replace />;
  }

  // If outlet selected but on selection page, redirect to devices
  if (selectedOutlet && location.pathname === '/select-outlet') {
    return <Navigate to="/devices" replace />;
  }

  // Show outlet selection page
  if (location.pathname === '/select-outlet') {
    return <OutletSelectionView />;
  }

  // Show main layout with sidebar
  return <MainLayout />;
}

export default App;
